import React, { useEffect, useState } from 'react';
import { Card, Col, Form, Radio, Row, Select, Space, Typography } from 'antd';
import './styles.css';
import {
  advertiserTypes,
  defaultIabCategory,
  iabCategories,
  regulatedCategories,
} from '../../../core/utils/constants/constants';
import CampaignRequestorLocationsDialog from './CampaignRequestorLocationsDialog';
import { CampaignRequestorRetailStoreTable } from './CampaignRequestorRetailStoreTable';

const { Text } = Typography;

const CampaignRequestorCategories = ({ formValues, helpers }) => {
  // Extract reactive values
  const { isRegulated, category, advertiserType, attributionTargets = [] } = formValues;

  const [showAdvertiserType, setShowAdvertiserType] = useState(false);
  const [showRetailStore, setShowRetailStore] = useState(false);
  const [showRetailStoresModal, setShowRetailStoresModal] = useState(false);
  const [originalAdvertiserType, setOriginalAdvertiserType] = useState(advertiserType);

  const handleIsRegulatedChange = (e) => {
    const value = e.target.value;
    helpers.updateField('isRegulated', value);
    
    if (!value) {
      helpers.setFieldsValue({ category: null, advertiserType: null });
      setShowAdvertiserType(false);
    } else {
      helpers.setFieldsValue({
        category: defaultIabCategory.code,
        advertiserType: originalAdvertiserType || 'LOCAL_DELIVERY_SERVICE',
      });
      setShowAdvertiserType(true);
    }
  };

  const handleCategoryChange = (value) => {
    helpers.updateField('category', value);

    if (value === 'IAB7-5') {
      setShowAdvertiserType(true);
    } else {
      setShowAdvertiserType(false);
      // For non-regulated categories, set a default advertiser type
      helpers.updateField('advertiserType', 'LOCAL_DELIVERY_SERVICE');
    }
  };

  const handleAdvertiserTypeChange = (value) => {
    helpers.updateField('advertiserType', value);
    setOriginalAdvertiserType(value);
    
    if (value === 'RETAIL_LOCAL_BRICK_AND_MORTAR') {
      setShowRetailStore(true);
    } else {
      setShowRetailStore(false);
    }
  };

  // Set up initial state based on reactive form values
  useEffect(() => {
    if (advertiserType && !originalAdvertiserType) {
      setOriginalAdvertiserType(advertiserType);
    }
    
    if (
      (Array.isArray(category) && category.includes(defaultIabCategory.code)) ||
      category === defaultIabCategory.code
    ) {
      setShowAdvertiserType(true);
    }
    
    if (advertiserType === 'RETAIL_LOCAL_BRICK_AND_MORTAR') {
      setShowRetailStore(true);
    }
  }, [category, advertiserType, originalAdvertiserType]);

  // Handle locations dialog selection
  const handleAddSelections = (targets) => {
    const attributionFieldValues = {
      measureWalkInTraffic: true,
      attributionTargets: targets,
    };
    helpers.setFieldsValue(attributionFieldValues);
  };

  return (
    <>
      <Card id="requester-categories" title="CATEGORIES">
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Text>Does this campaign promote a regulated industry?</Text>

                <Form.Item name="isRegulated">
                  <Radio.Group 
                    value={isRegulated}
                    onChange={handleIsRegulatedChange}
                  >
                    <Space direction="vertical">
                      <Radio value={true}>Yes</Radio>
                      <Radio value={false}>No</Radio>
                    </Space>
                  </Radio.Group>
                </Form.Item>
              </Space>
            </Col>
            <Col xs={24} md={12}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                {isRegulated ? (
                  <>
                    <Text>Which Regulated Industry does it promote?</Text>
                    <Form.Item name="category">
                      <Select
                        style={{ width: '100%' }}
                        showSearch
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                          option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }
                        options={regulatedCategories.map((iab) => ({
                          value: iab.code,
                          label: iab.category,
                        }))}
                        value={category}
                        onChange={handleCategoryChange}
                      />
                    </Form.Item>

                    {showAdvertiserType && (
                      <>
                        <Text>Advertiser Type</Text>
                        <Form.Item name="advertiserType">
                          <Select
                            style={{ width: '100%' }}
                            showSearch
                            optionFilterProp="children"
                            filterOption={(input, option) =>
                              option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
                            }
                            options={
                              advertiserTypes.map((advertiser) => ({
                                value: advertiser.key,
                                label: advertiser.name,
                              })) || []
                            }
                            value={advertiserType}
                            onChange={handleAdvertiserTypeChange}
                          />
                        </Form.Item>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <Text>Which category best fits this campaign?</Text>
                    <Form.Item name="category">
                      <Select
                        style={{ width: '100%' }}
                        showSearch
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                          option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }
                        options={iabCategories.map((iab) => ({
                          value: iab.code,
                          label: iab.category,
                        }))}
                        value={category}
                        onChange={handleCategoryChange}
                      />
                    </Form.Item>
                  </>
                )}
              </Space>
            </Col>
          </Row>

          {showRetailStore && (
            <div>
              <Row>
                <Col span={24}>
                  <CampaignRequestorRetailStoreTable
                    form={helpers.form}
                    editable={true}
                    setShowRetailStoresModal={setShowRetailStoresModal}
                    setPreviousValues={(values) => {
                      // Convert legacy setPreviousValues to helper methods
                      helpers.setFieldsValue(values);
                    }}
                  />
                </Col>
              </Row>
            </div>
          )}
        </Space>
      </Card>

      {showRetailStoresModal && (
        <CampaignRequestorLocationsDialog
          visible={showRetailStoresModal}
          setVisible={setShowRetailStoresModal}
          audienceType="ATTRIBUTION"
          onAddSelections={handleAddSelections}
          selectedLocations={attributionTargets}
        />
      )}
    </>
  );
};

export default CampaignRequestorCategories;